import { LoadingButton } from '@mui/lab';
import {
  Box,
  Grid,
  IconButton,
  // styled,
  TextField,
  Stack,
  Typography,
  Paper,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import { Transition } from '../transition';
import { useFormik } from 'formik';
import { ApiEndpoint, AppColors } from '../../utils/utils';
import bookDemoFormSchema from '../../validation/bookDemoFormSchema';
import {
  getCapitalizedString,
  showSuccessMessage,
} from '../../utils/utilMethods';
import { axiosInstance } from '../../client';

interface IBookDemoFormState {
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
}

interface IProps {
  toggle: boolean;
  onClose: () => void;
  onSubmitSuccess?: (code: string) => void;
}

const BookDemoFormModel: React.FC<IProps> = ({
  toggle,
  onClose,
  onSubmitSuccess,
}) => {
  const inputFieldLabelStyle = {
    color: AppColors.blackColor,
    mb: 0.5,
  };

  const textFieldStyle = {
    '& label': {
      color: AppColors.grayThreeColor,
    },
    '& .MuiOutlinedInput-root input': {
      color: AppColors.blackColor,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: ` ${AppColors.grayOneColor} !important`,
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: `${AppColors.primaryColor} !important`,
    },
    '& .MuiInputLabel-shrink': {
      backgroundColor: AppColors.grayZeroColor,
      paddingLeft: '5px',
      paddingRight: '5px',
      borderRadius: '5px',
    },
    '& .MuiOutlinedInput-root': {
      // background: AppColors.grayZeroColor,
      '& fieldset': {
        borderColor: AppColors.grayOneColor,
      },
      '&:hover fieldset': {
        borderColor: AppColors.grayOneColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: AppColors.grayOneColor,
      },
    },
  };

  const {
    handleSubmit,
    values,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
  } = useFormik<IBookDemoFormState>({
    initialValues: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
    },
    validationSchema: bookDemoFormSchema,
    onSubmit: async (values) => {
      try {
        const response = await axiosInstance.post(ApiEndpoint.REQUEST_DEMO, {
          ...values,
        });

        showSuccessMessage(
          `Thanks for your interest. An agent will be in touch with you shortly.`
        );

        if (response && response.data && response.data.data) {
          const sc = response.data.data.contact.contact_code;

          onSubmitSuccess && onSubmitSuccess(sc);
        }
        onClose();
        resetForm();
      } catch (error: any) {
        console.error(error);
      }
    },
  });

  return (
    <Dialog
      maxWidth='md'
      fullWidth
      open={toggle}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => {
        resetForm();
        onClose();
      }}
      aria-describedby='alert-dialog-slide-description'
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '12px', // Set your desired radius here
        },
      }}
    >
      <IconButton
        aria-label='close'
        onClick={() => {
          resetForm();
          onClose();
        }}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
          color: 'white',
          backgroundColor: 'red',
          '&:hover': {
            backgroundColor: 'rgba(255,0,0,0.7)',
          },
        }}
      >
        <CloseIcon fontSize='small' />
      </IconButton>
      {/* <BannerDialogTitle
        sx={{
          color: AppColors.primaryColor,
          textAlign: { md: 'start', xs: 'center' },
        }}
      >
        Book a Demo
      </BannerDialogTitle> */}
      <form onSubmit={handleSubmit} autoComplete='off'>
        <DialogContent
          sx={{
            padding: '24px',
            background: `url('/static/home_page/knocard_auto_banner_small.png')`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
        >
          <Paper sx={{ p: 2 }}>
            <Stack direction='column' spacing={2}>
              <Grid container gap={2} justifyContent={'center'}>
                <Grid size={{ xs: 12, md: 5.8 }}>
                  <Typography sx={inputFieldLabelStyle}>First Name*</Typography>
                  <TextField
                    id='firstName'
                    name='firstName'
                    fullWidth
                    onChange={handleChange}
                    value={values.firstName}
                    onInput={(e: any) => {
                      e.target.value = getCapitalizedString(
                        e.target.value ?? ''
                      );
                    }}
                    error={Boolean(errors.firstName && touched.firstName)}
                    helperText={
                      Boolean(errors.firstName && touched.firstName) &&
                      errors.firstName
                    }
                    sx={{ ...textFieldStyle }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 5.8 }}>
                  <Typography sx={inputFieldLabelStyle}>Last Name*</Typography>
                  <TextField
                    id='lastName'
                    name='lastName'
                    fullWidth
                    onChange={handleChange}
                    value={values.lastName}
                    onInput={(e: any) => {
                      e.target.value = getCapitalizedString(
                        e.target.value ?? ''
                      );
                    }}
                    error={Boolean(errors.lastName && touched.lastName)}
                    helperText={
                      Boolean(errors.lastName && touched.lastName) &&
                      errors.lastName
                    }
                    sx={{ ...textFieldStyle }}
                  />
                </Grid>
              </Grid>

              <Box>
                <Typography sx={inputFieldLabelStyle}>
                  Mobile Number*
                </Typography>
                <TextField
                  type='tel'
                  name='phone'
                  id='phone'
                  fullWidth
                  onChange={handleChange}
                  value={values.phone}
                  error={Boolean(errors.phone && touched.phone)}
                  helperText={
                    Boolean(errors.phone && touched.phone) && errors.phone
                  }
                  sx={{ ...textFieldStyle }}
                />
              </Box>

              <Box>
                <Typography sx={inputFieldLabelStyle}>Email*</Typography>
                <TextField
                  type='email'
                  name='email'
                  id='email'
                  fullWidth
                  onChange={handleChange}
                  value={values.email}
                  error={Boolean(errors.email && touched.email)}
                  helperText={
                    Boolean(errors.email && touched.email) && errors.email
                  }
                  sx={{ ...textFieldStyle }}
                />
              </Box>

              <Stack direction='column' spacing={2} justifyContent={'flex-end'}>
                <LoadingButton
                  sx={{
                    minWidth: '100px',
                    borderColor: AppColors.blueColor,
                    backgroundColor: AppColors.blueColor,
                    color: '#ffffff',
                    // textTransform: 'unset',
                    fontWeight: '600',
                    '& span span': {
                      color: '#ffffff',
                    },
                  }}
                  type='submit'
                  loading={isSubmitting}
                  variant='outlined'
                >
                  Submit
                </LoadingButton>
                {/* <LoadingButton
                      sx={{
                        minWidth: '80px',
                        color: AppColors.grayThreeColor,
                        textTransform: 'unset',
                        fontWeight: '600',
                        mt: '10px !important',
                      }}
                      type='reset'
                      variant='text'
                      onClick={() => {
                        resetForm();
                        onClose();
                      }}
                    >
                      Close
                    </LoadingButton> */}
              </Stack>
            </Stack>
          </Paper>
        </DialogContent>
      </form>
    </Dialog>
  );
};

export default BookDemoFormModel;
